import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { compareStrings } from '../shared/utils';
import { dateToString } from '../shared/formatters';

var today = new Date();

const initialState = {
  items: [],
  shouldBeUpdated: true,
  lastUpdate: today.toISOString(),
  filterFields: {
    description: '',
    categories: [],
    groups: [],
    financeAccounts: [],
    users: [],
    from: dateToString(new Date(today.getFullYear(), today.getMonth(), 1)),
    to: dateToString(new Date(today.getFullYear(), today.getMonth() + 1, 0))
  },
  order: { by: 'date', direction: 'desc' }
};

// Async thunks
export const loadTransaction = createAsyncThunk(
  'transaction/loadTransaction',
  async (_, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      const filters = getState().transaction.filterFields;
      const result = await api.loadTransaction(filters);
      dispatch({ type: 'appMain/loadingEnd' });
      return { items: result.items };
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const addTransaction = createAsyncThunk(
  'transaction/addTransaction',
  async (item, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      await api.addTransaction(item);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: 'Transação criada com sucesso!',
          severity: 'success',
          type: 'success'
        } 
      });
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const updateTransaction = createAsyncThunk(
  'transaction/updateTransaction',
  async (item, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      await api.updateTransaction(item);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: 'Transação atualizada com sucesso!',
          severity: 'success',
          type: 'success'
        } 
      });
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const deleteTransaction = createAsyncThunk(
  'transaction/deleteTransaction',
  async (id, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      await api.deleteTransaction(id);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: 'Transação excluída com sucesso!',
          severity: 'success',
          type: 'success'
        } 
      });
      return {};
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const addTransfer = createAsyncThunk(
  'transaction/addTransfer',
  async (transfer, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {

      const creditTransactionData = {
        'description': transfer.description,
        'value': transfer.value,
        'categoryId': transfer.creditTransferCategoryId,
        'financeAccountId': transfer.financeAccountDestinationId,
        'date': transfer.date
      };
      const debitTransactionData = {
        'description': transfer.description,
        'value': transfer.value,
        'categoryId': transfer.debitTransferCategoryId,
        'financeAccountId': transfer.financeAccountOriginId,
        'date': transfer.date
      };
      await api.addTransaction(creditTransactionData);
      await api.addTransaction(debitTransactionData);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: 'Transações criadas com sucesso!',
          severity: 'success',
          type: 'success'
        } 
      });
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

const transactionSlice = createSlice({
  name: 'transaction',
  initialState,
  reducers: {
    updateFilters: (state, action) => {
      state.shouldBeUpdated = true;
      state.filterFields = { ...action.payload };
    },
    updateTransactionOrder: (state, action) => {
      const sortedItems = [...state.items].sort((a, b) => {
        switch (action.payload.by) {
          case 'priority':
            return a.priority - b.priority;
          case 'account':
            return compareStrings(a.financeAccount.name, b.financeAccount.name);
          case 'description':
            return compareStrings(a.description, b.description);
          case 'category':
            return compareStrings(a.category.name, b.category.name);
          case 'date':
            return compareStrings(a.date, b.date);
          case 'value':
            return compareStrings(a.value, b.value);
          default:
            return compareStrings(a.date, b.date);
        }
      });
      if (action.payload.direction === 'desc') {
        sortedItems.reverse();
      }
      state.items = sortedItems;
      state.order = action.payload;
    },
    clear: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTransaction.fulfilled, (state, action) => {
        const sortedItems = [...action.payload.items].sort((a, b) => {
          switch (state.order.by) {
            case 'priority':
              return a.priority - b.priority;
            case 'account':
              return compareStrings(a.financeAccount.name, b.financeAccount.name);
            case 'description':
              return compareStrings(a.description, b.description);
            case 'category':
              return compareStrings(a.category.name, b.category.name);
            case 'date':
              return compareStrings(a.date, b.date);
            case 'value':
              return compareStrings(a.value, b.value);
            default:
              return compareStrings(a.date, b.date);
          }
        });
        if (state.order.direction === 'desc') {
          sortedItems.reverse();
        }
        state.items = sortedItems;
        state.shouldBeUpdated = false;
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(loadTransaction.rejected, (state) => {
        state.shouldBeUpdated = true;
      })
      // Add transaction cases
      .addCase(addTransaction.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      })
      // Update transaction cases
      .addCase(updateTransaction.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      })
      // Delete transaction cases
      .addCase(deleteTransaction.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      })
      // Add transfer cases
      .addCase(addTransfer.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      });
  }
});

export const { updateFilters, toogleFilters, updateTransactionOrder, clear , clearDefaultValue} = transactionSlice.actions;

export default transactionSlice.reducer;
